import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, ModalBody, ModalCloseButton, ModalHeader } from '@televet/kibble-ui/build/components/Modal';
import * as Sentry from '@sentry/react';
import { Divider, HStack, TabPanel, VStack } from '@televet/kibble-ui/build/chakra';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import {
  AppointmentDraft,
  closeAppointmentModal,
  setOriginalAppointment,
  updateAppointmentDraft,
} from 'pages/Appointments/state/appointmentsModalSlice';
import { AppointmentPet } from './components/AppointmentPet';
import { AppointmentType } from './components/AppointmentType';
import { AppointmentDateTime } from './components/AppointmentDateTime';
import { AppointmentProvider } from './components/AppointmentProvider';
import { AppointmentRoom } from './components/AppointmentRoom';
import { AppointmentAutomations } from './components/AppointmentAutomations';
import { AppointmentNotes } from './components/AppointmentNotes';
import { TabsLayout } from '@televet/kibble-ui';
import { Spinner } from '@televet/kibble-ui/build/components/Spinner';
import { TextInput } from '@televet/kibble-ui/build/components/TextInput';
import { SqsQueueNames } from '@televet/shared-types/SqsQueueNames';
import { useGetAppointmentModalAppointmentQuery, WorkflowEventSetting } from 'shared/types/graphql';
import { ConditionEngineRoutingKey, FindWorkflowsForContextJobInput } from '@televet/shared-types/conditionEngineJobs';
import useClinicUser from 'shared/hooks/useClinicUser';
import { useTriggerMessageEventAndSubscribe } from 'shared/hooks/useTriggerMessageEventAndSubscribe';
import { AppointmentModalFooter } from './components/AppointmentModalFooter';
import DevModeDisplayJSON from 'pages/Settings/components/DevModeDisplayJSON';
import { DEFAULT_APPOINTMENT_DURATION_IN_MINUTES } from 'shared/providers/ApolloProvider/reactiveVars/appointmentFilters';
import { AiRecapNotes } from './components/AiRecapNotes';
import { NewAppointmentModal } from '../NewAppointmentModal';
import { AiRecapTranscription } from './components/AiRecapTranscription';
import useFeatureFlag from 'shared/hooks/useFeatureFlag';
import { FeatureFlagName, GraphQLFetchPolicies } from 'shared/enums';
import { ModuleName, useModularity } from 'shared/hooks/useModularity';
import { nonStandardAppointmentNoClientName, nonStandardAppointmentNoPatientName } from './constants';

export type LinkedAutomationUpdates = Partial<
  Pick<AppointmentDraft, 'appointmentTypeId' | 'clinicEmployeeId' | 'roomId' | 'petId'>
>;

export function AppointmentModal(): JSX.Element {
  const dispatch = useAppDispatch();
  const { recapNotes, petName, petParentName } = useAppSelector((state) => state.appointmentsModal.appointmentDraft);
  const {
    isOpen,
    appointmentId,
    appointmentDraft: { appointmentTypeId, clinicEmployeeId, roomId, petId },
    mode,
  } = useAppSelector((state) => state.appointmentsModal);
  const { currentClinicId } = useClinicUser();
  const { isFeatureEnabled } = useFeatureFlag();
  const { hasModule } = useModularity();
  const isStandardClinic = hasModule(ModuleName.AppointmentsStandard);
  const [isLoadingLinkedAutomations, setIsLoadingLinkedAutomations] = useState(false);

  const hasAppointmentPageImprovements = useMemo(
    () => isFeatureEnabled(FeatureFlagName.AppointmentsPageImprovements),
    [isFeatureEnabled],
  );

  const { data: appointmentData, loading: loadingAppointmentData } = useGetAppointmentModalAppointmentQuery({
    variables: {
      where: {
        id: appointmentId,
      },
    },
    skip: !appointmentId,
    onCompleted: ({ findUniqueAppointment: appointment }) => {
      if (!appointment) return;

      const appointmentDraft: AppointmentDraft = {
        clinicEmployeeId: appointment.clinicEmployees.length ? appointment.clinicEmployees[0].id : undefined,
        roomId: appointment.room?.id || undefined,
        petId: appointment.clinicPetId || undefined,
        petParentIds: appointment.clinicPetParents.map((petParent) => petParent.id) || [],
        selectedPet: appointment.clinicPet || undefined,
        appointmentTypeId: appointment.appointmentType?.id || undefined,
        startAt: appointment.startAt,
        durationInMinutes: appointment.durationInMinutes || DEFAULT_APPOINTMENT_DURATION_IN_MINUTES,
        description: appointment.description,
        recapNotes: appointment.recapNotes || undefined,
        selectedAutomationIds: appointment.workflows.map((workflow) => workflow.id),
        pimsId: appointment.pimsId || undefined,
        aiRecapWriteback: appointment.aiRecapPimsWritebackLogEntry[0]?.writebackLog,
        status: appointment.status,
        channelStatus: appointment.channel?.channelStatus || undefined,
        channelId: appointment.channel?.id || undefined,
        clinicEmployee: appointment.clinicEmployees[0] || undefined,
        appointmentType: appointment.appointmentType || undefined,
        room: appointment.room || undefined,
        tags: appointment.channel?.tags || [],
      };

      if (!isStandardClinic) {
        const firstClinicPetParent = appointment?.clinicPetParents?.[0];
        appointmentDraft.petName = appointment.clinicPet?.name === nonStandardAppointmentNoPatientName
          ? ''
          : appointment.clinicPet?.name;

        appointmentDraft.petParentName = firstClinicPetParent?.firstName === nonStandardAppointmentNoClientName
          ? ''
          : `${firstClinicPetParent?.firstName || ''} ${firstClinicPetParent?.lastName || ''}`.trim();
      }

      dispatch(updateAppointmentDraft(appointmentDraft));
      dispatch(setOriginalAppointment(appointmentDraft));
    },
    fetchPolicy: GraphQLFetchPolicies.NoCache,
  });

  const onCloseModal = useCallback(() => {
    dispatch(closeAppointmentModal());
  }, [dispatch]);

  const { triggerMessageEvent, payload: jobChainPayload } =
    useTriggerMessageEventAndSubscribe<Pick<WorkflowEventSetting, 'id'>[]>();

  // Match workflows to this appointment by calling the condition engine with appointment context
  const updateLinkedAutomations = useCallback(
    async (updates: LinkedAutomationUpdates) => {
      if (!currentClinicId) return;

      setIsLoadingLinkedAutomations(true);

      const appointmentContext: FindWorkflowsForContextJobInput = {
        appointmentTypeId: updates.appointmentTypeId || appointmentTypeId,
        clinicEmployeeIds: updates.clinicEmployeeId
          ? [updates.clinicEmployeeId]
          : clinicEmployeeId
            ? [clinicEmployeeId]
            : [],
        clinicRoomId: updates.roomId || roomId,
        clinicPetId: updates.petId || petId,
      };

      try {
        await triggerMessageEvent({
          variables: {
            queueName: SqsQueueNames.ConditionEngineJobs,
            event: ConditionEngineRoutingKey.FindWorkflowsForContext,
            useSubscription: true,
            data: {
              clinicId: currentClinicId,
              ...appointmentContext,
            },
          },
        });
      } catch (error) {
        setIsLoadingLinkedAutomations(false);
        console.error('Error triggering message event: ', error);
        Sentry.captureException(error);
      }
    },
    [appointmentTypeId, clinicEmployeeId, currentClinicId, petId, roomId, triggerMessageEvent],
  );

  useEffect(() => {
    if (jobChainPayload?.status === 'completed') {
      // TODO: Fix BE-sourced JSON structure to be a top-level Array rather than a nested, int-keyed Object
      const matchingWorkflowIds = Object.values(jobChainPayload.payload?.[0] || {}).map(({ id }) => id);
      dispatch(updateAppointmentDraft({ selectedAutomationIds: matchingWorkflowIds }));
    }
    if (jobChainPayload?.status === 'completed' || jobChainPayload?.status === 'errored') {
      setIsLoadingLinkedAutomations(false);
    }
  }, [dispatch, jobChainPayload]);

  const tabList = useMemo(
    () => [
      {
        label: 'AI Recap Notes',
        color: 'text.default',
        flex: 1,
      },
      {
        label: 'AI Recap Transcription',
        color: 'text.default',
        flex: 1,
      },
    ],
    [],
  );

  return (
    <Modal isOpen={isOpen} onClose={onCloseModal} size="xl" scrollBehavior="inside" dataTestId="appointment-modal">
      {loadingAppointmentData && <Spinner isFullScreen />}
      {hasAppointmentPageImprovements && mode === 'view' && <NewAppointmentModal />}
      {(!hasAppointmentPageImprovements || mode !== 'view') && (
        <>
          <ModalHeader className="AppointmentModal__Header" size="lg">
            {appointmentId ? `Appointment Details` : `Create Appointment`}
          </ModalHeader>
          <ModalCloseButton className="AppointmentModal__CloseButton" />
          <ModalBody className="AppointmentModal__Body" maxH={['100%', '100%', '600px']}>
            <VStack
              align="flex-start"
              spacing={4}
              w="100%"
              className="AppointmentModal__Details"
              data-testid="appointment-modal-form"
            >
              {isStandardClinic ? (
                <>
                  <AppointmentPet updateLinkedAutomations={updateLinkedAutomations} />
                  <Divider />
                  <AppointmentType updateLinkedAutomations={updateLinkedAutomations} />
                  <AppointmentDateTime />
                  <HStack w="100%" align="flex-start">
                    <AppointmentProvider updateLinkedAutomations={updateLinkedAutomations} />
                    <AppointmentRoom updateLinkedAutomations={updateLinkedAutomations} />
                  </HStack>
                  <AppointmentNotes />
                  <Divider />
                  {recapNotes && (
                    <TabsLayout
                      width="100%"
                      tabList={tabList}
                      tabPanels={[
                        <TabPanel key={'ai_recap-notes'}>
                          <AiRecapNotes />
                        </TabPanel>,
                        <TabPanel key={'ai_recap-transcription'}>
                          <AiRecapTranscription />
                        </TabPanel>,
                      ]}
                    />
                  )}
                  <AppointmentAutomations isLoadingLinkedAutomations={isLoadingLinkedAutomations} />
                </>
              ) : (
                  <>
                    <TextInput
                      label="Patient Name"
                      onChange={(e): void => {
                        dispatch(
                          updateAppointmentDraft({
                            petName: e.target.value,
                          }),
                        );
                      }}
                      value={petName || ''}
                      data-testid="pet-name-input"
                      placeholder="Enter patient name"
                      autoFocus={!appointmentId}
                    />
                    <TextInput
                      label="Client Name"
                      onChange={(e): void => {
                        dispatch(updateAppointmentDraft({ petParentName: e.target.value }));
                      }}
                      value={petParentName || ''}
                      data-testid="pet-parent-name-input"
                      placeholder="Enter client name"
                    />
                    <Divider />
                    <AppointmentDateTime />
                    <Divider />
                    {recapNotes && (
                      <TabsLayout
                        width="100%"
                        tabList={tabList}
                        tabPanels={[
                          <TabPanel key={'ai_recap-notes'}>
                            <AiRecapNotes />
                          </TabPanel>,
                          <TabPanel key={'ai_recap-transcription'}>
                            <AiRecapTranscription />
                          </TabPanel>,
                        ]}
                      />
                    )}
                  </>
                )}
            </VStack>
            <DevModeDisplayJSON src={appointmentData} />
          </ModalBody>
          <AppointmentModalFooter />
        </>
      )}
    </Modal>
  );
}
